import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useOperator } from '@/lib/operator-context';
import { apiFetch } from '@/lib/operator-api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sparkles,
  Zap,
  Scale,
  Crown,
  BarChart3,
  ThumbsUp,
  MessageSquare,
  TrendingUp,
  DollarSign,
  AlertCircle,
  Loader2,
  Save,
  RefreshCw,
  Info,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

type ModelTier = 'FAST' | 'BALANCED' | 'QUALITY';

interface CopilotConfig {
  id?: string;
  businessId: string;
  enabled: boolean;
  modelTier: ModelTier;
  maxSuggestionsPerMessage: number;
  autoGenerateOnIncoming: boolean;
  allowedIntents: string[];
}

interface CopilotUsageStats {
  suggestionsGenerated: number;
  suggestionsAccepted: number;
  suggestionsDismissed: number;
  suggestionsExpired: number;
  acceptanceRate: number;
  costThisMonth: number;
  costLastMonth: number;
  topIntents: Array<{ intent: string; count: number }>;
}

// ============================================================================
// Constants
// ============================================================================

interface ModelTierInfo {
  tier: ModelTier;
  label: string;
  description: string;
  icon: typeof Zap;
  color: string;
  badgeClass: string;
}

const MODEL_TIERS: ModelTierInfo[] = [
  {
    tier: 'FAST',
    label: 'Fast',
    description: 'Quick responses with lower cost. Best for simple greetings and confirmations.',
    icon: Zap,
    color: 'text-amber-600',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  {
    tier: 'BALANCED',
    label: 'Balanced',
    description: 'Good quality at moderate cost. Handles most conversation types well.',
    icon: Scale,
    color: 'text-blue-600',
    badgeClass: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    tier: 'QUALITY',
    label: 'Quality',
    description: 'Highest quality responses. Best for complex situations and complaint resolution.',
    icon: Crown,
    color: 'text-purple-600',
    badgeClass: 'bg-purple-100 text-purple-700 border-purple-200',
  },
];

const DEFAULT_CONFIG: CopilotConfig = {
  businessId: '',
  enabled: false,
  modelTier: 'BALANCED',
  maxSuggestionsPerMessage: 3,
  autoGenerateOnIncoming: true,
  allowedIntents: [],
};

// ============================================================================
// API Helpers
// ============================================================================

const copilotApi = {
  async getConfig(businessId: string): Promise<CopilotConfig> {
    const res = await apiFetch(
      `/api/operator/copilot/config?businessId=${encodeURIComponent(businessId)}`
    );
    if (!res.ok) throw new Error(`Failed to load config: ${res.status}`);
    const data = await res.json();
    return data.config || data;
  },

  async updateConfig(config: Partial<CopilotConfig> & { businessId: string }): Promise<CopilotConfig> {
    const res = await apiFetch('/api/operator/copilot/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    });
    if (!res.ok) throw new Error(`Failed to save config: ${res.status}`);
    const data = await res.json();
    return data.config || data;
  },

  async getUsage(businessId: string): Promise<CopilotUsageStats> {
    const res = await apiFetch(
      `/api/operator/copilot/usage?businessId=${encodeURIComponent(businessId)}`
    );
    if (!res.ok) throw new Error(`Failed to load usage stats: ${res.status}`);
    const data = await res.json();
    return data.usage || data;
  },
};

// ============================================================================
// Sub-components
// ============================================================================

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
  color,
}: {
  icon: typeof BarChart3;
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
}) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-border">
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-foreground">{value}</p>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

function CopilotSettingsPage() {
  const { t } = useTranslation();
  const { operator, isLoading: authLoading, selectedBusinessId, selectedBusiness } = useOperator();

  const [config, setConfig] = useState<CopilotConfig>(DEFAULT_CONFIG);
  const [usage, setUsage] = useState<CopilotUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load config and usage stats
  const loadData = useCallback(async (signal?: AbortSignal) => {
    if (!selectedBusinessId) return;
    setLoading(true);
    setError(null);

    try {
      const [configData, usageData] = await Promise.all([
        copilotApi.getConfig(selectedBusinessId),
        copilotApi.getUsage(selectedBusinessId),
      ]);
      if (signal?.aborted) return;
      setConfig({ ...DEFAULT_CONFIG, ...configData, businessId: selectedBusinessId });
      setUsage(usageData);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (signal?.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load copilot settings');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [selectedBusinessId]);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  // Clear success message after delay
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  // Update config field and mark dirty
  const updateConfig = useCallback(
    <K extends keyof CopilotConfig>(field: K, value: CopilotConfig[K]) => {
      setConfig((prev) => ({ ...prev, [field]: value }));
      setHasChanges(true);
      setSaveSuccess(false);
    },
    []
  );

  // Save config
  const handleSave = useCallback(async () => {
    if (!selectedBusinessId) return;
    setSaving(true);
    setError(null);
    setSaveSuccess(false);

    try {
      const saved = await copilotApi.updateConfig({
        businessId: selectedBusinessId,
        enabled: config.enabled,
        modelTier: config.modelTier,
        maxSuggestionsPerMessage: config.maxSuggestionsPerMessage,
        autoGenerateOnIncoming: config.autoGenerateOnIncoming,
      });
      setConfig({ ...DEFAULT_CONFIG, ...saved, businessId: selectedBusinessId });
      setHasChanges(false);
      setSaveSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [selectedBusinessId, config]);

  // Loading / auth states
  if (authLoading) {
    return <LoadingSpinner />;
  }

  if (!operator) return null;

  if (!selectedBusinessId) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Sparkles className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              Select a business to configure AI Copilot settings.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-[hsl(160,84%,39%)]" />
            AI Copilot Settings
          </h1>
          <p className="text-muted-foreground">
            {selectedBusiness?.name && `${selectedBusiness.name} - `}
            Configure AI-powered suggestion assistant for conversations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => loadData()}
            disabled={loading}
            className="gap-1.5 border-[hsl(160,60%,18%)]/20"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="gap-1.5 bg-[hsl(160,60%,18%)] hover:bg-[hsl(160,60%,22%)] text-white"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving...' : t('common.save')}
          </Button>
        </div>
      </div>

      {/* Success / error banners */}
      {saveSuccess && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2">
          <ThumbsUp className="h-4 w-4 text-emerald-600" />
          <p className="text-sm text-emerald-700">Settings saved successfully.</p>
        </div>
      )}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <p className="text-sm text-red-600">{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="ml-auto h-7 text-xs"
            onClick={() => loadData()}
          >
            Retry
          </Button>
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Enable/Disable toggle */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-[hsl(160,84%,39%)]" />
                      AI Copilot
                    </CardTitle>
                    <CardDescription>
                      When enabled, AI will generate reply suggestions for incoming
                      customer messages.
                    </CardDescription>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => updateConfig('enabled', checked)}
                  />
                </div>
              </CardHeader>
            </Card>

            {/* Model tier selector */}
            <Card className={!config.enabled ? 'opacity-50 pointer-events-none' : ''}>
              <CardHeader>
                <CardTitle className="text-lg">Model Tier</CardTitle>
                <CardDescription>
                  Choose the AI model quality level. Higher tiers produce better
                  suggestions but cost more.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {MODEL_TIERS.map((tier) => {
                    const TierIcon = tier.icon;
                    const isSelected = config.modelTier === tier.tier;

                    return (
                      <button
                        key={tier.tier}
                        type="button"
                        onClick={() => updateConfig('modelTier', tier.tier)}
                        className={`group relative p-4 rounded-xl border-2 text-left transition-all duration-200 hover:shadow-md ${
                          isSelected
                            ? 'border-[hsl(160,84%,39%)] bg-[hsl(160,84%,39%)]/5 shadow-sm'
                            : 'border-border hover:border-[hsl(160,60%,18%)]/30'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <div className="w-5 h-5 rounded-full bg-[hsl(160,84%,39%)] flex items-center justify-center">
                              <svg
                                className="w-3 h-3 text-white"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-colors ${
                            isSelected
                              ? 'bg-[hsl(160,84%,39%)]/10'
                              : 'bg-muted group-hover:bg-muted/80'
                          }`}
                        >
                          <TierIcon
                            className={`h-5 w-5 ${isSelected ? 'text-[hsl(160,60%,18%)]' : tier.color}`}
                          />
                        </div>
                        <h3 className="font-semibold text-foreground mb-1">
                          {tier.label}
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {tier.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Additional settings */}
            <Card className={!config.enabled ? 'opacity-50 pointer-events-none' : ''}>
              <CardHeader>
                <CardTitle className="text-lg">Suggestion Settings</CardTitle>
                <CardDescription>
                  Fine-tune how AI suggestions are generated and displayed.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Max suggestions per message */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">
                      Max Suggestions Per Message
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Number of suggestion options generated for each incoming message.
                    </p>
                  </div>
                  <Select
                    value={String(config.maxSuggestionsPerMessage)}
                    onValueChange={(val) =>
                      updateConfig('maxSuggestionsPerMessage', parseInt(val, 10))
                    }
                  >
                    <SelectTrigger className="w-20 border-[hsl(160,60%,18%)]/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {/* Auto-generate toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium">
                      Auto-Generate on Incoming Messages
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically generate suggestions when a new customer
                      message arrives.
                    </p>
                  </div>
                  <Switch
                    checked={config.autoGenerateOnIncoming}
                    onCheckedChange={(checked) =>
                      updateConfig('autoGenerateOnIncoming', checked)
                    }
                  />
                </div>

                <Separator />

                {/* Info note */}
                <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-blue-700 leading-relaxed">
                    Suggestions are generated based on conversation context,
                    customer history, and business information. Operators can
                    always edit suggestions before sending.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column: Usage stats */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-[hsl(160,84%,39%)]" />
                  Usage Statistics
                </CardTitle>
                <CardDescription>
                  This month&apos;s AI Copilot activity
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {usage ? (
                  <>
                    <StatCard
                      icon={MessageSquare}
                      label="Suggestions Generated"
                      value={usage.suggestionsGenerated.toLocaleString()}
                      subtitle="Total AI suggestions created"
                      color="bg-[hsl(160,84%,39%)]/10 text-[hsl(160,60%,18%)]"
                    />
                    <StatCard
                      icon={ThumbsUp}
                      label="Suggestions Accepted"
                      value={usage.suggestionsAccepted.toLocaleString()}
                      subtitle="Used by operators"
                      color="bg-emerald-100 text-emerald-700"
                    />
                    <StatCard
                      icon={TrendingUp}
                      label="Acceptance Rate"
                      value={`${Math.round(usage.acceptanceRate * 100)}%`}
                      subtitle={
                        usage.acceptanceRate >= 0.7
                          ? 'Great adoption'
                          : usage.acceptanceRate >= 0.4
                            ? 'Moderate adoption'
                            : 'Low adoption - consider adjusting tier'
                      }
                      color="bg-blue-100 text-blue-700"
                    />
                    <StatCard
                      icon={DollarSign}
                      label="Cost This Month"
                      value={`R$ ${usage.costThisMonth.toFixed(2)}`}
                      subtitle={
                        usage.costLastMonth > 0
                          ? `Last month: R$ ${usage.costLastMonth.toFixed(2)}`
                          : 'First month of usage'
                      }
                      color="bg-purple-100 text-purple-700"
                    />

                    {/* Top intents breakdown */}
                    {usage.topIntents && usage.topIntents.length > 0 && (
                      <>
                        <Separator />
                        <div>
                          <h4 className="text-sm font-medium text-foreground mb-3">
                            Top Intents
                          </h4>
                          <div className="space-y-2">
                            {usage.topIntents.slice(0, 5).map((item) => {
                              const total = usage.suggestionsGenerated || 1;
                              const pct = Math.round((item.count / total) * 100);
                              return (
                                <div key={item.intent} className="space-y-1">
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">
                                      {item.intent
                                        .replace(/_/g, ' ')
                                        .toLowerCase()
                                        .replace(/^\w/, (c: string) => c.toUpperCase())}
                                    </span>
                                    <span className="font-medium text-foreground">
                                      {item.count}
                                    </span>
                                  </div>
                                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                                    <div
                                      className="h-full rounded-full bg-[hsl(160,84%,39%)] transition-all duration-300"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8">
                    <BarChart3 className="h-10 w-10 text-muted-foreground/30 mb-3" />
                    <p className="text-sm text-muted-foreground text-center">
                      No usage data available yet.
                    </p>
                    <p className="text-xs text-muted-foreground text-center mt-1">
                      Enable AI Copilot to start tracking usage.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Current configuration summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Current Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge
                      variant="outline"
                      className={
                        config.enabled
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-gray-50 text-gray-500 border-gray-200'
                      }
                    >
                      {config.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Model Tier</span>
                    <Badge
                      variant="outline"
                      className={
                        MODEL_TIERS.find((t) => t.tier === config.modelTier)
                          ?.badgeClass || ''
                      }
                    >
                      {config.modelTier}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Max Suggestions</span>
                    <span className="font-medium text-foreground">
                      {config.maxSuggestionsPerMessage}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Auto-Generate</span>
                    <span className="font-medium text-foreground">
                      {config.autoGenerateOnIncoming ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_auth/operator/copilot-settings')({
  component: CopilotSettingsPage,
});
